"use client";

import { useState } from "react";
import { FiCheck, FiAlertCircle, FiArrowRight } from "react-icons/fi";
import { useAuth } from "@/hooks/useAuth";
import { celebrateBurst } from "@/lib/confetti";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";

interface Position {
  conditionId: string;
  tokenId: string;
  marketSlug: string;
  marketQuestion: string;
  outcome: string;
  outcomeIndex: number;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  invested: number;
  currentValue: number;
}

interface MergePositionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  yesPosition: Position;
  noPosition: Position;
  maxMerge: number;
  onSuccess?: () => void;
}

export default function MergePositionsModal({
  isOpen,
  onClose,
  yesPosition,
  noPosition,
  maxMerge,
  onSuccess,
}: MergePositionsModalProps) {
  const { isLoggedIn } = useAuth();
  const [amount, setAmount] = useState<string>(maxMerge.toFixed(2));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const mergeAmount = parseFloat(amount) || 0;
  const isValid = mergeAmount > 0 && mergeAmount <= maxMerge;

  const handleMerge = async () => {
    if (!isLoggedIn || !isValid) return;

    setLoading(true);
    setError(null);

    try {
      const res = await authFetch("/api/trade/merge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conditionId: yesPosition.conditionId,
          amount: mergeAmount,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setSuccess(true);
        celebrateBurst();
        toast.success(`Merged ${mergeAmount.toFixed(2)} sets → $${mergeAmount.toFixed(2)} USDC`);
        onSuccess?.();
      } else {
        setError(data.error || "Merge failed");
      }
    } catch (e: any) {
      setError(e.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount(maxMerge.toFixed(2));
    setError(null);
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  // Cost basis being recovered
  const yesCostPerShare = yesPosition.avgPrice;
  const noCostPerShare = noPosition.avgPrice;
  const totalCostBasis = (yesCostPerShare + noCostPerShare) * mergeAmount;
  const netResult = mergeAmount - totalCostBasis;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-sm bg-white dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Merge Positions
            </h3>
            <button
              onClick={handleClose}
              className="p-1 rounded-[5px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
            {yesPosition.marketQuestion}
          </p>
        </div>

        {success ? (
          /* Success state */
          <div className="px-5 pb-5">
            <div className="flex flex-col items-center py-6">
              <div className="w-12 h-12 rounded-[5px] bg-emerald-500/10 flex items-center justify-center mb-3">
                <FiCheck className="w-6 h-6 text-emerald-500" />
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Positions merged
              </p>
              <p className="text-2xl font-bold text-emerald-500 mt-1">
                +${mergeAmount.toFixed(2)} USDC
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {mergeAmount.toFixed(2)} YES + NO sets burned
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-full py-2.5 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] text-gray-900 dark:text-white text-sm font-medium hover:bg-gray-200 dark:hover:bg-[#222] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          /* Form state */
          <div className="px-5 pb-5 space-y-4">
            {/* Position balances */}
            <div className="space-y-2">
              <div className="flex items-center justify-between px-3 py-2.5 rounded-[5px] bg-gray-50 dark:bg-[#0a0a0b]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-[5px] bg-emerald-500" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">Yes</span>
                </div>
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {yesPosition.shares.toFixed(2)} shares
                </span>
              </div>
              <div className="flex items-center justify-between px-3 py-2.5 rounded-[5px] bg-gray-50 dark:bg-[#0a0a0b]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-[5px] bg-red-500" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">No</span>
                </div>
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {noPosition.shares.toFixed(2)} shares
                </span>
              </div>
            </div>

            {/* Amount input */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-gray-500 dark:text-gray-400">Sets to merge</label>
                <button
                  onClick={() => setAmount(maxMerge.toFixed(2))}
                  className="text-[10px] text-blue-500 hover:text-blue-400 font-medium"
                >
                  Max: {maxMerge.toFixed(2)}
                </button>
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min={0}
                max={maxMerge}
                step={0.01}
                className="w-full px-3 py-2.5 rounded-[5px] bg-gray-50 dark:bg-[#0a0a0b] border border-gray-200 dark:border-[#1A1A1A] text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Preview */}
            {isValid && (
              <div className="px-3 py-3 rounded-[5px] bg-emerald-500/5 border border-emerald-500/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <span>{mergeAmount.toFixed(2)} YES + NO</span>
                    <FiArrowRight className="w-3 h-3" />
                  </div>
                  <span className="text-sm font-semibold text-emerald-500">
                    ${mergeAmount.toFixed(2)} USDC
                  </span>
                </div>
                {totalCostBasis > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    Cost basis: ${totalCostBasis.toFixed(2)} · Net: {netResult >= 0 ? '+' : ''}{netResult.toFixed(2)}
                  </p>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-[5px] bg-red-500/5 border border-red-500/10">
                <FiAlertCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-500">{error}</p>
              </div>
            )}

            {/* Action button */}
            <button
              onClick={handleMerge}
              disabled={!isValid || loading}
              className="w-full py-2.5 rounded-[5px] bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Merging...
                </span>
              ) : (
                `Merge ${isValid ? mergeAmount.toFixed(2) : ''} Positions`
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
